import {api, urls} from '@/api'
import local from '@/localStorage'
import * as types from '@/store/mutation-types'
import errors from '@/apiHandleErrors'
import {authSuccessful, checkResponse} from '@/store/helpers'

const getInitialState = () => ({
  awaiting: true,
  /** Email from API */
  email: '',
  /** Whether user is authenticated or not */
  isAuth: false,
  /** Bearer token from authentication */
  bearer: null,
  /** Refresh token from authentication */
  refresh: null,
  favorites: [],
  id: null,
  type: '',
  person: {},
  phones: [],
  emails: [],
  locations: [],
  checking_account: {},
  checkingAccountMovements: [],
  checkingAccountPagination: {},
  following: [],
  followingPagination: {},
  publications: [],
  page: 1,
  per_page: 25,
  totalPages: null,
  aggregations: {},
  publicationsPendingQuestions: [],
  publicationsAnsweredQuestions: [],
  notifications: [],
  notificationsPage: 1,
  notificationsCheckpoint: null,
  newUnansweredQuestions: 0,
  mercadopago_linked_account: false,
  marketplace: false,
 })

const state = getInitialState()

const getters = {
  isCommerce: state => state.type === 'COMMERCE',

  marketplaceEnabled: state => state.marketplace === true,

  person: (state, getters) => {
    const complement = getters.isCommerce
      ? state.person.legal_person || {}
      : state.person.physical_person || {}

    return complement
  },

  name: (state, getters) => {
    const person = state.person

    if (!getters.isCommerce) {
      return person.physical_person && person.physical_person.first_name
    } else {
      return person.legal_person && person.legal_person.fantasy_name
    }
  },

  email: (state, getters) => {
    return state.email
  }

}

const actions = {
  login: ({commit}, formData) => api.post(urls.login, formData)
    .then(authSuccessful(commit))
    .catch(errors.login),

  logout: ({commit}) => api.post(urls.logout)
    .then(({data}) => {
      console.info(data.message)

      commit(types.SET_USER_TOKEN, null)
      commit(types.SET_USER_REFRESH, null)
      commit(types.USER_RESET_STATE)

      local.removeTokens()
    })
    .catch(err => {
      const status = err.response.status

      if (status === 401) {
        commit(types.SET_USER_TOKEN, null)
        commit(types.SET_USER_REFRESH, null)
        commit(types.USER_RESET_STATE)

        local.removeTokens()
      }
    }),

  setTokens: ({commit}, {token, refresh}) => {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`
    commit(types.SET_USER_TOKEN, token)
    commit(types.SET_USER_REFRESH, refresh)

    if (!state.isAuth) {
      commit(types.USER_LOGIN)
      commit(types.USER_SET_AWAITING, false)
    }
  },

  refresh: ({commit}) => {
    if (!!state.refresh) {
      return api.post(urls.refresh, {refresh_token: state.refresh})
        .then(authSuccessful(commit))
        .catch(err => {
          commit(types.SET_USER_TOKEN, null)
          commit(types.SET_USER_REFRESH, null)
          commit(types.USER_RESET_STATE)
          local.removeTokens()
          return Promise.reject(err)
        })
    } else {
      commit(types.SET_USER_TOKEN, null)
      commit(types.SET_USER_REFRESH, null)
      commit(types.USER_RESET_STATE)
      local.removeTokens()
    }
  },

  register: ({commit}, formData) => api.post(urls.register, formData)
    .then(({data}) => {}),

  registerVerifyAccount: ({commit}, token) => api.get(`${urls.registerVerifyAccount}/${token}`)
    .catch((err) => { throw new Error(err.response.status) }),

  resendRegistrationEmail: ({commit}, formData) => api.post(urls.registerResend, formData),

  verifyEmail: ({commit}, token) => api.get(`${urls.userEmailVerify}/${token}`)
    .then(({data}) => {})
    .catch((err) => { throw new Error(err.response.status) }),

  changePassword: (state, formData) => api.post('/api/password/reset', formData),

  checkIfAuth: ({commit, state}) => new Promise((resolve, reject) => {
    const check = () => {
      setTimeout(() => state.awaiting ? check() : resolve(state.isAuth), 200)
      setTimeout(() => resolve(false), 2000)
    }
    check()
  }),

  // -------------- user info CRUD -----------------

  fetchUserInfo: ({commit, dispatch, rootState}, params) => {
    const isAuth = rootState.user.isAuth
    if (!isAuth) return Promise.resolve();
    return api.get(urls.me, { params })
      .then(({data}) => {
        commit(types.USER_SET_LOCATIONS, data.data.locations)
        commit(types.USER_SET_PHONES, data.data.phones)
        commit(types.USER_SET_EMAILS, data.data.emails)
        commit(types.USER_SET_NOTIFICATIONS_CHECKPOINT, data.data.pending_notifications)
        commit(types.SET_USER_INFO, data.data)

        const checkingAccount = data.data.checking_account
        checkingAccount && commit('SET_USER_CHECKING_ACCOUNT', checkingAccount)

        // sets lat & lon
        const location = data.data.preferred_location

        if (location && location.city && location.coordinates && location.coordinates.latitude && location.coordinates.longitude) {
          const lat = parseFloat(location.coordinates.latitude)
          const lon = parseFloat(location.coordinates.longitude)
          const city = location.city

          const preferredLocation = {lat, lon, city}
          const localStorageLocation = local.getLocation()

          if (preferredLocation.lat !== localStorageLocation.lat ||
            preferredLocation.lon !== localStorageLocation.lon ||
            preferredLocation.city.toString() !== localStorageLocation.city) {
            local.setLocation(preferredLocation)
            commit(types.SET_LOCATION, preferredLocation, {root: true})
          }
        }
      })
      .catch(err => {
        if (err.response && err.response.data && err.response.data.code === 401) {
          dispatch('refresh')
        }
      })
  },

  updateUserInfo: ({commit}, info) => api.post(urls.me, info),

  // --------- locations CRUD -------------

  storeLocation: ({commit}, location) => {
    const loc = {
      latitude: location.lat,
      longitude: location.lon,
      city: location.city,
      string: location.string,
      public: true
    }

    return api.post(urls.userLocations, loc)
      .then(({data}) => { return true })
  },

  removeLocation: ({commit}, loc) => {
    commit(types.USER_SET_LOCATIONS, state.locations.filter(l => l.id !== loc.id))

    return api.delete(`${urls.userLocations}/${loc.id}`)
      .then(({data}) => { return true })
      .catch((err) => { throw err })
  },

  updateLocation: ({commit}, loc) => api.put(`${urls.userLocations}/${loc.id}`, loc)
    .then(({data}) => {}),

  // --------- phones CRUD -------------

  storePhone: ({commit}, phone) => api.post(urls.userPhones, phone)
    .then(({data}) => {}),

  removePhone: ({commit, state}, phone) => {
    commit(types.USER_SET_PHONES, state.phones.filter(p => p.id !== phone.id))

    return api.delete(`${urls.userPhones}/${phone.id}`)
      .then(({data}) => {
        return true
      })
      .catch((err) => {
        throw err
      })
  },

  updatePhone: ({commit}, phone) => api.put(`${urls.userPhones}/${phone.id}`, phone)
    .then(({data}) => {}),

  sendPhone: ({commit}, {phone, type, transfer}) => api.post(urls.sendSMS, {contact_number: phone, phone_type: type, transfer: transfer})
    .then(({data}) => data)
    .catch(er => {
      if (er.response.status === 400) {
        const code = er.response.data && er.response.data.error.message && er.response.data.error.message.code

        return {code}
      } else {
        return Promise.reject(er)
      }
    }),

  verifyPhone: ({commit}, {phone, code, availability, transfer}) => {
    const formData = { contact_number: phone, code, availability, transfer: transfer }

    return api.post(urls.verifySMS, formData)
      .then(({data}) => {})
  },

  // Password recovery via SMS/CALL
  sendRecoveryCode: ({commit}, {email, type}) => api.post(urls.sendCode, {email: email, phone_type: type})
    .then(({data}) => data)
    .catch(er => {
      if (er.response.status === 400) {
        const code = er.response.data && er.response.data.error.message && er.response.data.error.message.code
        return {code}
      } else {
        return Promise.reject(er)
      }
    }),

  sendNewPassword: ({commit}, {code, password}) => api.post(urls.sendNewPassword, {code: code, password: password})
    .then(({data}) => {})
    .catch(er => {
      if (er.response.status === 400) {
        const code = er.response.data && er.response.data.error.message && er.response.data.error.message.code
        return {code}
      } else {
        return Promise.reject(er)
      }
    }),

  // ------------------ emails CRUD --------------------
  storeEmail: ({commit}, email) => api.post(urls.userEmails, email),

  removeEmail: ({commit}, email) => {
    commit(types.USER_SET_EMAILS, state.emails.filter(e => e.id !== email.id))

    return api.delete(`${urls.userEmails}/${email.id}`)
      .then(({data}) => { return true })
      .catch((err) => {
        throw err
      })
  },

  toggleEmail: ({commit}, email) => api.put(`${urls.userEmails}/${email.id}`, email)
    .then(({data}) => {}),

  updateEmail: ({commit}, email) => api.post(urls.me, email),

  // ----------- favorites CRUD -----------------

  fetchFavorites: ({commit, rootState}) => {
    const params = {
      favorites: rootState.user.id,
      per_page: 50
    }
    return api.get(urls.publicationSearch, { params })
      .then(({data}) => {
        commit(types.SET_USER_FAVORITES, data.data)
      })
      .catch(err => { console.error('fetchFavorites', err) })
  },

  addFavorite: ({dispatch}, id) => {
    return api.post(urls.favorites, { publication_id: id })
  },

  removeFavorite: ({dispatch}, id) => {
    return api.delete(`${urls.favorites}/${id}`)
  },

  // ------------------------

  uploadAvatar: ({commit, dispatch}, file) => {
    const formData = new FormData()
    formData.append('image_file_name', file)

    return api.post(urls.userAvatar, formData)
  },

  fetchCheckingAccountMovements: ({commit}, params) => api.get(urls.checkingAccountMovements, {params})
    .then(({data}) => {
      commit(types.USER_SET_CHECKING_ACCOUNT_MOVEMENTS, data.data)
      commit(types.USER_SET_CHECKING_ACCOUNT_PAGINATION, data.meta && data.meta.pagination)
      return true
    })
    .catch(er => {}),

  fetchMovementDetails: ({commit}, id) => api.get(`${urls.checkingAccountMovements}/${id}`)
    .then(({data}) => {}),

  resetCheckingAccountState: ({commit}) => {
    commit(types.USER_SET_CHECKING_ACCOUNT_MOVEMENTS, [])
    commit(types.USER_SET_CHECKING_ACCOUNT_PAGINATION, {})
    return true
  },

  fetchFollowing: ({commit, state}, params) => api.get(urls.profileFollowing, { params: { ...params, user_id: state.id } })
    .then(({data}) => {
      commit(types.USER_SET_FOLLOWING, data.data)
      commit(types.USER_SET_FOLLOWING_PAGINATION, data.meta.pagination)
    })
    .catch(er => {}),

  unfollow: ({commit, state}, userId) => {
    commit(types.USER_SET_FOLLOWING, state.following.filter(({id}) => id !== userId))

    return api.delete(`${urls.profileFollowing}/${userId}`)
  },

  fetchFollowedPublications: ({commit}, params) => api.get(urls.followedPublications, { params })
    .then(({data}) => {
      const pagination = data.meta.pagination

      commit('category/' + types.SET_CAT_PUBS, data.data, { root: true })
      commit('category/' + types.SET_CAT_TOTAL_PAGES, pagination.total_pages, { root: true })
      commit('category/' + types.SET_CAT_TOTAL_PUBS, pagination.total, { root: true })

      return data
    }),

  // ---------- questions answers CRUD ---------------
  fetchPendingQuestions: ({commit, rootState}, params) => {
    const isAuth = rootState.user.isAuth
    if (!isAuth) return Promise.resolve();
    return api.get(urls.pendingQuestions, { params })
      .then(({data}) => {
        commit(types.USER_SET_NEW_UNANSWERED_QUESTION, 0)
        commit(types.USER_SET_PUBLICATIONS_PENDING_QUESTIONS, data.data)
        return data.meta.pagination
      })
      .catch(er => {})
  },

  fetchAnsweredQuestions: ({commit}, params) => api.get(urls.answeredQuestions, { params })
    .then(({data}) => {
      commit(types.USER_SET_PUBLICATIONS_ANSWERED_QUESTIONS, data.data)
      return data.meta.pagination
    })
    .catch(er => {}),

  createAnswer: ({commit, dispatch}, {id, answer}) => {
    const formData = { question_id: id, answer }
    // return Promise.resolve()
    return api.post(urls.publicationAnswers, formData)
  },

  // --------- notifications CRUD ----------------

  fetchNotifications: ({commit, state, rootState}) => {
    const isAuth = rootState.user.isAuth
    // Prevent API call if not authenticated
    if (!isAuth) return Promise.resolve();
    const params = {page: state.notificationsPage}

    return api.get(urls.notifications, {params})
      .then(checkResponse)
      .then(({data, next}) => {
        commit(types.USER_APPEND_NOTIFICATIONS, data)
        if (next) {
          commit(types.USER_NOTIFICATIONS_NEXT_PAGE)
        }
        return next
      })
  },

  addNotification: ({commit}, notification) => {
    commit(types.USER_ADD_NOTIFICATION, notification)
  },

  updateNotification: ({commit}, {id, key, val}) => api.patch(`${urls.notifications}/${id}`)
    .then(() => {
      commit(types.USER_UPDATE_NOTIFICATION, {id, key, val})
      return true
    })
    .catch(er => {}),

  setAllNotificationsAsSeen: ({commit, state}) => api.patch(urls.userNotificationsSetAllSeen)
    .then(() => {
      const val = new Date()

      state.notifications
        .filter(n => !n.read_at)
        .forEach(({id}) => commit(types.USER_UPDATE_NOTIFICATION, { id, key: 'read_at', val }))
    })
    .catch(err =>{}),

  addNewUnansweredQuestion: ({commit, state}) => {
    const newValue = state.newUnansweredQuestions + 1
    commit(types.USER_SET_NEW_UNANSWERED_QUESTION, newValue)
  },

  // ---------- publication CRUD ------------------

  fetchPublications: ({commit, state}, params = {}) => {
    if (params.page) commit(types.USER_SET_PAGE, params.page)
    else params.page = state.page

    params.per_page = state.per_page

    return api.get(urls.userPublications, { params })
      .then(({data}) => {
        const aggregations = Array.isArray(data.aggregations) ? {} : data.aggregations

        commit(types.USER_SET_PUBLICATIONS, data.data)
        commit(types.USER_SET_AGGREGATIONS, aggregations)
        commit(types.USER_SET_TOTAL_PAGES, data.meta.pagination.total_pages)
      })
      .catch(er => {})
  },

  setPage: ({commit}, page) => { commit(types.USER_SET_PAGE, page) },

  resetPublications: ({commit}) => {
    commit(types.USER_SET_PUBLICATIONS, [])
    return Promise.resolve()
  },

  convertToAdditional: ({commit}, {id}) => {
    commit(types.USER_CONVERT_TO_ADDITIONAL, id)

    return api.put(`${urls.userPublications}/${id}`, {publication_type: 'additional'})
      .then(({data}) => {
        return true
      })
      .catch(er => { return Promise.reject(er) })
  },

  setPubStatus: ({commit}, {id, status}) => {
    commit(types.USER_SET_PUBLICATION_STATUS, {id, status})

    return api.put(`${urls.userPublications}/${id}`, {publication_status: status})
      .then(({data}) => {
        return true
      })
      .catch(er => { return Promise.reject(er) })
  },

  deletePublication: ({commit, state}, pubId) => {
    commit(types.USER_SET_PUBLICATIONS, state.publications.filter(({id}) => id !== pubId))

    return api.delete(`${urls.userPublications}/${pubId}`)
  },

  republishRequest: ({commit}, token) => api.get(`${urls.publicationRepublish}/${token}`),

  deleteToken: ({commit}, token) => api.get(`${urls.publicationDelete}/${token}`),

  republishPublication: ({commit, state}, id) => api.patch(`${urls.publications}/${id}/republish`)
    .then(res => { return true })
    .catch(err => { return Promise.reject(err) }),

  toggleAutoRepublish: ({commit, state}, {id, autoRepublish}) => {
    const formData = {
      auto_republication: autoRepublish
    }

    return api.put(`${urls.publications}/${id}/auto-republish`, formData)
      .then(res => {
        commit(types.USER_TOGGLE_REPUBLISH, id)
        return true
      })
      .catch(err => { return Promise.reject(err) })
  },

  sendPasswordEmail: ({commit}, email) => api.post(urls.passwordEmail, { email })
    .then(({data}) => { return true }),

  setNotificationsCheckpoint: ({commit}) => api.patch(urls.userNotifications)
    .then(() => {
      const d = new Date()
      const month = d.getMonth()+1 < 10 ? `0${d.getMonth()+1}` : `${d.getMonth()+1}`
      const day = d.getDate() < 10 ? `0${d.getDate()}` : `${d.getDate()}`
      const hours = d.getHours() < 10 ? `0${d.getHours()}` : `${d.getHours()}`
      const minutes = d.getMinutes() < 10 ? `0${d.getMinutes()}` : `${d.getMinutes()}`
      const seconds = d.getSeconds() < 10 ? `0${d.getSeconds()}` : `${d.getSeconds()}`
      const newDate = `${d.getFullYear()}-${month}-${day} ${hours}:${minutes}:${seconds}`
      commit(types.USER_SET_NOTIFICATIONS_CHECKPOINT, newDate)
      return true
    }),

  deleteAnswer: ({commit, dispatch}, {id, answer}) => {
    return api.delete(`${urls.userAnswers}/${answer}`)
      .then(() => { dispatch('publication/getQuestions', id, { root: true }) })
      .catch((err) => { console.error('Error while deleting answer: ', err) })
  },

  downloadReport: ({commit, state}, {baseUrl, query}) => {
    // store.dispatch('setIsFetching', true)
    return fetch(`${urls.host}${baseUrl}${query}`, {
      method: 'get',
      headers: new Headers({
        'Authorization': 'Bearer ' + state.bearer
      })
    })
      .then(resp => {
        if (resp.status !== 200) {
          throw new Error('Falló la descarga del archivo.')
        } else {
          return resp.blob()
        }
      })
      .then(blob => {
        const now = new Date()
        const month = now.getMonth()+1 < 10 ? `0${now.getMonth()+1}` : now.getMonth()+1
        const day = now.getDate() < 10 ? `0${now.getDate()}` : now.getDate()
        const date = `${now.getFullYear()}-${month}-${day}_${now.getHours()}_${now.getMinutes()}_${now.getSeconds()}`
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = url
        // the filename you want
        a.download = `maspocovendo-preguntas-recibidas-${date}.xlsx`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        return true
      })
      .catch(() => alert('Falló la descarga del reporte!'))
      .finally(() => {
        // store.dispatch('setIsFetching', false)
      })
  },

  setMarketplace: ({commit}, marketplace) => { commit(types.USER_SET_MARKETPLACE, marketplace) },
}

const mutations = {
  [types.USER_RESET_STATE] (state) {
    Object.assign(state, getInitialState())
  },

  [types.USER_LOGIN] (state) {
    state.isAuth = true
  },

  [types.USER_LOGOUT] (state) {
    state.isAuth = false
  },

  /**
   * Sets bearer token.
   *
   * @param {string} token
   */
  [types.SET_USER_TOKEN] (state, token) {
    state.bearer = token
  },

  /**
   * Sets refresh token.
   *
   * @param {string} token
   */
  [types.SET_USER_REFRESH] (state, token) {
    state.refresh = token
  },

  /**
   * @param {object} userinfo
   */
  [types.SET_USER_INFO] (state, userinfo) {
    // state.name = userinfo.name
    state.email = userinfo.registration_email
    state.id = userinfo.id
    state.type = userinfo.type
    state.person = userinfo.person
    state.mercadopago_linked_account = userinfo.mercadopago_linked_account
    state.marketplace = userinfo.marketplace
  },

  [types.SET_USER_CHECKING_ACCOUNT] (state, data) {
    state.checking_account = data
  },

  [types.SET_USER_FAVORITES] (state, favs) {
    state.favorites = favs || []
  },

  [types.USER_SET_PUBLICATIONS] (state, publications) {
    state.publications = publications
  },

  [types.USER_CONVERT_TO_ADDITIONAL] (state, id) {
    const index = state.publications.findIndex(p => p.id === id)
    state.publications[index].publication_type = 'additional'
  },

  [types.USER_SET_AGGREGATIONS] (state, aggregations) {
    state.aggregations = aggregations
  },

  [types.USER_SET_AWAITING] (state, status) {
    state.awaiting = status
  },

  [types.USER_SET_CHECKING_ACCOUNT_MOVEMENTS] (state, movements) {
    state.checkingAccountMovements = movements || []
  },

  [types.USER_SET_CHECKING_ACCOUNT_PAGINATION] (state, pagination) {
    state.checkingAccountPagination = pagination || {}
  },

  [types.USER_SET_FOLLOWING] (state, following) {
    state.following = following || []
  },
  [types.USER_SET_FOLLOWING_PAGINATION] (state, pagination) {
    state.followingPagination = pagination || {}
  },

  [types.USER_SET_PUBLICATIONS_PENDING_QUESTIONS] (state, publications) {
    state.publicationsPendingQuestions = publications
  },

  [types.USER_SET_PUBLICATIONS_ANSWERED_QUESTIONS] (state, publications) {
    state.publicationsAnsweredQuestions = publications
  },

  [types.USER_SET_NOTIFICATIONS] (state, notifications) {
    state.notifications = notifications || []
  },

  [types.USER_APPEND_NOTIFICATIONS] (state, notifications) {
    state.notifications = [...state.notifications, ...notifications]
  },

  [types.USER_NOTIFICATIONS_NEXT_PAGE] (state) {
    state.notificationsPage += 1
  },

  [types.USER_ADD_NOTIFICATION] (state, notification) {
    state.notifications = [notification, ...state.notifications]
  },

  [types.USER_UPDATE_NOTIFICATION] (state, {id, key, val}) {
    state.notifications = state.notifications
      .map(n => n.id !== id ? n : { ...n, [key]: val })
  },

  [types.USER_SET_PUBLICATION_STATUS] (state, {id, status}) {
    const statusList = {
      1: 'ENABLED',
      2: 'HIDDEN',
      3: 'BLOCKED',
      4: 'AWAITING_PAYMENT',
      5: 'AWAITING_REVISION',
      6: 'UNFINISHED',
      7: 'TO DELETED'
    }

    state.publications = state.publications
      .map(p => p.id !== id ? p : {...p, status: statusList[status]})
  },

  [types.USER_SET_PHONES] (state, phones) {
    state.phones = phones || []
  },

  [types.USER_SET_EMAILS] (state, emails) {
    state.emails = emails || []
  },

  [types.USER_SET_LOCATIONS] (state, locations) {
    state.locations = locations || []
  },

  [types.USER_SET_PAGE] (state, page) {
    state.page = parseInt(page)
  },

  [types.USER_SET_TOTAL_PAGES] (state, total) {
    state.totalPages = total
  },

  [types.USER_TOGGLE_REPUBLISH] (state, id) {
    state.publications = state.publications
      .map(p => p.id !== id ? p : {...p, auto_republication: !p.auto_republication})
  },

  [types.USER_SET_NOTIFICATIONS_CHECKPOINT] (state, date) {
    state.notificationsCheckpoint = date
  },

  [types.USER_SET_NEW_UNANSWERED_QUESTION] (state, value) {
    state.newUnansweredQuestions = value
  },

  [types.USER_SET_MARKETPLACE] (state, marketplace) {
    state.marketplace = marketplace
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/user.js